import React, { useState } from 'react';
import styled from 'styled-components';
import { FaQuoteLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useI18n } from '../i18n';

const TestimonialsSection = () => {
  const { t } = useI18n();
  const testimonials = t.testimonials.items;

  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <Section>
      <div className="container">
        <SectionHeader>
          <Badge>{t.testimonials.badge}</Badge>
          <Title>{t.testimonials.title}</Title>
        </SectionHeader>
        
        <TestimonialContainer>
          <QuoteIcon>
            <FaQuoteLeft />
          </QuoteIcon>
          
          <TestimonialSlider>
            <TestimonialContent>
              {testimonials[currentIndex].content}
            </TestimonialContent>
            
            <AuthorInfo>
              <Avatar aria-hidden>{testimonials[currentIndex].author.charAt(0)}</Avatar>
              <AuthorText>
                <AuthorName>{testimonials[currentIndex].author}</AuthorName>
                <AuthorPosition>{testimonials[currentIndex].position}</AuthorPosition>
              </AuthorText>
            </AuthorInfo>
          </TestimonialSlider>
          
          <SliderControls>
            <SliderButton onClick={handlePrev}>
              <FaChevronLeft />
            </SliderButton>
            <SliderDots>
              {testimonials.map((_, index) => (
                <SliderDot 
                  key={index} 
                  active={index === currentIndex}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </SliderDots>
            <SliderButton onClick={handleNext}>
              <FaChevronRight />
            </SliderButton>
          </SliderControls>
        </TestimonialContainer>
      </div>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  padding: 5.5rem 0;
  background:
    radial-gradient(800px 360px at 90% 10%, #f0f8fd 0%, transparent 60%),
    white;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 2.75rem;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.42rem 1rem;
  background: var(--primary-light);
  color: #0a6b93;
  font-weight: 800;
  font-size: 0.76rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  border-radius: 999px;
  margin-bottom: 0.9rem;

  &::before {
    content: '';
    width: 0.42rem;
    height: 0.42rem;
    border-radius: 50%;
    background: var(--primary-color);
  }
`;

const Title = styled.h2`
  font-size: clamp(1.9rem, 3.4vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.035em;
  color: var(--text-dark);
  margin: 0;
`;

const TestimonialContainer = styled.div`
  position: relative;
  overflow: hidden;
  max-width: 900px;
  margin: 0 auto;
  padding: 3.25rem 3rem 2.75rem;
  background:
    radial-gradient(500px 240px at 100% 0%, rgba(0, 166, 232, 0.08) 0%, transparent 65%),
    white;
  border: 1px solid var(--border);
  border-radius: 26px;
  box-shadow: var(--shadow-md);

  @media (max-width: 640px) {
    padding: 2.25rem 1.5rem 2rem;
  }
`;

const QuoteIcon = styled.div`
  font-size: 5rem;
  color: var(--primary-color);
  opacity: 0.12;
  position: absolute;
  top: 1.25rem;
  right: 2rem;
  line-height: 1;
  pointer-events: none;
`;

const TestimonialSlider = styled.div`
  position: relative;
  text-align: left;
  z-index: 1;
`;

const TestimonialContent = styled.blockquote`
  font-size: clamp(1.3rem, 2.4vw, 1.75rem);
  line-height: 1.5;
  color: var(--text-dark);
  font-weight: 500;
  letter-spacing: -0.015em;
  margin: 0 0 2rem;
  max-width: 44rem;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 2rem;
`;

const Avatar = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.9rem;
  height: 2.9rem;
  border-radius: 50%;
  background: var(--gradient-brand);
  color: white;
  font-size: 1.1rem;
  font-weight: 800;
  box-shadow: 0 8px 18px rgba(0, 142, 204, 0.28);
`;

const AuthorText = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0 0 0.15rem;
`;

const AuthorPosition = styled.p`
  font-size: 0.95rem;
  color: var(--text-muted);
  margin: 0;
`;

const SliderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
`;

const SliderButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: white;
  border: 1px solid var(--border);
  color: var(--text-dark);
  font-size: 0.85rem;
  padding: 0;
  box-shadow: var(--shadow-xs);
  transition: all 0.25s var(--ease);

  &:hover {
    color: white;
    background: var(--primary-color);
    border-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: 0 8px 16px rgba(0, 142, 204, 0.24);
  }
`;

const SliderDots = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SliderDot = styled.button<{ active: boolean}>`
  width: ${(props) => (props.active ? '1.5rem' : '0.5rem')};
  height: 0.5rem;
  border-radius: 999px;
  background: ${(props) => (props.active ? 'var(--primary-color)' : 'rgba(12, 27, 42, 0.18)')};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s var(--ease);

  &:hover {
    background: var(--primary-color);
  }
`;

export default TestimonialsSection; 